// The day: navigation, the board hook (60 s polling, D6), the grid,
// the drawer a tap opens (1.5), the five actions inside it and the
// same-day drag (1.6) with its one-line confirm (day-view.md §7).
// After every write the board is read fresh, and the open drawer's
// detail with it.

import {useCallback, useEffect, useRef, useState} from "react";
import {ApiRequestError, formatDateTr, getDay, istToday, shiftDate} from "../api/client";
import type {BoardCell, BoardClient, DayBoard, Me} from "../api/contract";
import {Grid} from "../grid/Grid";
import {overlapsAt, toMin} from "../grid/layout";
import {CANCELLED_BY_TR, WA_REPLY_TR, seansState} from "../labels";
import {useToasts} from "../ui/toast-context";
import {useActions, type ClientRef} from "../actions/useActions";
import {useSessionDetail, type DetailKey} from "../actions/useSessionDetail";
import {
  ChannelSheet, DuplicateDialog, MoveSheet, NoteSheet, PaySheet,
  UndoRecordSheet,
} from "../actions/Sheets";
import {
  HistorySheet, NotesSheet, SeansInfoSheet, SessionDrawer, WaSheet, type Anchor, type DrawerActions,
} from "../actions/Drawer";
import {ClientSheet} from "../actions/ClientSheet";
import {DevirSheet, PayoutSheet} from "../actions/PayoutSheets";
import {AddClientSheet, AddSessionSheet, type AddSessionPreset} from "../actions/AddSheets";
import {DEMO_CLIENT, DEMO_DESK, demoDetail} from "../dev/fixture";
import {Icon} from "../ui/Icon";
import {useMoney} from "../ui/blind-context";
import {KasaSheet} from "./KasaDrawer";
import {ProviderSheet} from "./ProviderDrawer";
import {WaDaySheet} from "./WaDaySheet";

const REFRESH_MS = 60_000;

interface BoardState {
  date: string;
  board: DayBoard | null;
  at: Date | null; // when the board was read
  error: string | null;
  busy: boolean;
}

const blank = (date: string, busy: boolean): BoardState =>
  ({date, board: null, at: null, error: null, busy});

function useBoard(date: string) {
  const [state, setState] = useState<BoardState>(() => blank(date, true));
  // A state for another date is a stale one: the new date is loading.
  const current = state.date === date ? state : blank(date, true);

  const load = useCallback(async (fresh: boolean, signal?: AbortSignal) => {
    try {
      const {data} = await getDay(date, fresh, signal);
      if (signal?.aborted) return;
      setState({date, board: data, at: new Date(), error: null, busy: false});
    } catch (e) {
      if (signal?.aborted) return;
      const message = e instanceof ApiRequestError ?
        `${e.message} (${e.code})` : e instanceof Error ? e.message : String(e);
      // Keep the last good board on the screen under the error line.
      setState((s) => ({
        ...(s.date === date ? s : blank(date, false)),
        error: message, busy: false,
      }));
    }
  }, [date]);

  useEffect(() => {
    if (!date) return; // the demo: nothing to load
    const ctl = new AbortController();
    // load() only sets state after its await — the rule cannot see that.
    // oxlint-disable-next-line react/set-state-in-effect
    void load(false, ctl.signal);
    const timer = setInterval(() => void load(false, ctl.signal), REFRESH_MS);
    return () => {
      ctl.abort();
      clearInterval(timer);
    };
  }, [load, date]);

  const refresh = useCallback(() => {
    setState((s) => ({...(s.date === date ? s : blank(date, true)), busy: true}));
    void load(true);
  }, [date, load]);

  return {...current, refresh};
}

// Minutes into the Istanbul day, ticking once a minute — the now-line.
const istMinuteOfDay = (): number => {
  const d = new Date(Date.now() + 3 * 3600e3);
  return d.getUTCHours() * 60 + d.getUTCMinutes();
};

function useNowMin(): number {
  const [nowMin, setNowMin] = useState(istMinuteOfDay);
  useEffect(() => {
    const t = setInterval(() => setNowMin(istMinuteOfDay()), 60_000);
    return () => clearInterval(t);
  }, []);
  return nowMin;
}

// What is open over the grid: the drawer on a cell or a cancelled
// slot, or one of the sheets that open over it — which go back to it.
type Open =
  | {kind: "drawer"; anchor: Anchor}
  | {kind: "pay"; cell: BoardCell; client: BoardClient}
  | {kind: "undo"; cell: BoardCell; client: BoardClient}
  | {kind: "channel"; cell: BoardCell; client: BoardClient; to: "cash" | "bank"}
  // The move's confirm: from a drag (back to the grid on cancel) or
  // from the drawer's "Saati değiştir" (back to the drawer).
  | {kind: "move"; cell: BoardCell; time: string; fromDrag: boolean}
  | {kind: "note"; anchor: Anchor; client: ClientRef}
  | {kind: "client"; anchor: Anchor; client: ClientRef}
  // The drawer's own: WhatsApp onayı, Seans bilgisi, Seanslar ve
  // ödemeler, Notlar.
  | {kind: "wa"; anchor: Anchor}
  | {kind: "seansInfo"; anchor: Anchor}
  | {kind: "history"; anchor: Anchor; client: ClientRef}
  | {kind: "notes"; anchor: Anchor; client: ClientRef}
  // The Kasa drawer, from the bar; a danışman's day, from her name
  // above the column or her row in the Kasa drawer; Ver's sheets, from
  // either of them, and back to it.
  | {kind: "money"}
  | {kind: "provider"; providerId: string; fromMoney: boolean}
  | {kind: "payout"; providerId: string; back: Open}
  | {kind: "devir"; back: Open}
  // The day's WhatsApp send, from the bar.
  | {kind: "waDay"}
  // Seans ekle and Yeni danışan, from the bar (Onur, 03.11.2026); the
  // one opens the other and comes back with the new card picked.
  | {kind: "addSession"; preset?: AddSessionPreset}
  | {kind: "addClient"; name?: string; fromSession: boolean}
  | null;

// What opens from the bar, not over a seans.
const BARE = new Set(["money", "provider", "waDay", "payout", "devir", "addSession", "addClient"]);

const anchorOf = (o: Exclude<NonNullable<Open>,
  {kind: "money" | "provider" | "waDay" | "payout" | "devir" | "addSession" | "addClient"}>):
  Anchor =>
  o.kind === "pay" || o.kind === "move" ||
  o.kind === "undo" || o.kind === "channel" ? {cell: o.cell} : o.anchor;

// Another day from a round calendar button (Onur, 15.10.2026: the
// title already says the date, a date field beside it said it twice).
// The field stays in the page, invisible under the button, so the
// browser's own calendar opens there. Some browsers (Safari on the
// iPad) keep that calendar up until the field loses focus, and a tap
// on the page doesn't take it away (Onur, 17.10.2026: it only went
// when scrolled off), so a press anywhere else blurs the field.
function DatePick({date, onPick}: {date: string; onPick: (date: string) => void}) {
  const field = useRef<HTMLInputElement>(null);
  const [shown, setShown] = useState(false);
  const hide = () => {
    field.current?.blur();
    setShown(false);
  };
  const open = () => {
    setShown(true);
    try {
      field.current?.showPicker();
    } catch {
      field.current?.focus(); // a browser without showPicker: the field takes typing
    }
  };
  useEffect(() => {
    if (!shown) return;
    const away = (e: PointerEvent) => {
      if (!field.current?.parentElement?.contains(e.target as Node)) hide();
    };
    const esc = (e: KeyboardEvent) => e.key === "Escape" && hide();
    document.addEventListener("pointerdown", away, true);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("pointerdown", away, true);
      document.removeEventListener("keydown", esc);
    };
  }, [shown]);
  return (
    <span className="date-pick">
      <button className="round" onClick={open} aria-label="Tarih seç" title="Tarih seç">
        <Icon name="cal" />
      </button>
      <input ref={field} type="date" value={date} tabIndex={-1} aria-hidden="true"
        onBlur={() => setShown(false)}
        onChange={(e) => {
          if (!e.target.value) return;
          onPick(e.target.value);
          hide();
        }} />
    </span>
  );
}

export function Today({me, demo}: {me: Me; demo?: DayBoard}) {
  const [date, setDate] = useState(demo?.date ?? istToday());
  const live = useBoard(demo ? "" : date);
  // Dev only: a fixture day instead of the door, actions that do nothing.
  const {board, at, error, busy, refresh} = demo ?
    {board: demo, at: null, error: null, busy: false, refresh: () => undefined} : live;
  const nowMin = useNowMin();
  const today = demo ? demo.date : istToday();
  const [open, setOpen] = useState<Open>(null);
  const close = useCallback(() => setOpen(null), []);
  const toast = useToasts();
  const {tl} = useMoney();
  // "N seansın kaydı yok": each tap opens the next of them.
  const nextUnrecorded = useRef(0);
  // After a write: the board fresh, and the open drawer's detail again.
  const [detailVersion, setDetailVersion] = useState(0);
  const onDone = useCallback(() => {
    refresh();
    setDetailVersion((v) => v + 1);
  }, [refresh]);
  const actions = useActions(onDone, !!demo);

  // The seans the open sheet is about — the drawer's read, kept across
  // the action sheets so coming back to the drawer does not reload it.
  const anchor = open && !BARE.has(open.kind) ?
    anchorOf(open as Parameters<typeof anchorOf>[0]) : null;
  const detailKey: DetailKey | null = !anchor ? null : anchor.cell ?
    {providerId: anchor.cell.providerId, eventId: anchor.cell.id,
      date: anchor.cell.start.slice(0, 10)} :
    {providerId: anchor.rec.providerId, eventId: anchor.rec.eventId,
      date: board?.date ?? date};
  const detail = useSessionDetail(detailKey, detailVersion, demo ? demoDetail : undefined);

  // The open seans as the last poll drew it. When a poll changes it —
  // a guardian answered, someone took a payment on another screen —
  // the drawer reads its detail again (Onur, 22.10.2026: it stayed as
  // it was until a page reload).
  const openCellId = anchor?.cell?.id ?? "";
  const openSig = openCellId ?
    JSON.stringify(board?.cells.find((c) => c.id === openCellId) ?? null) : "";
  const lastSig = useRef({id: "", sig: ""});
  useEffect(() => {
    const last = lastSig.current;
    lastSig.current = {id: openCellId, sig: openSig};
    if (openCellId && last.id === openCellId && last.sig !== openSig) {
      setDetailVersion((v) => v + 1);
    }
  }, [openCellId, openSig]);

  // A guardian's answer that came with this poll: one toast each, with
  // a tap to the seans. The first board of a day only sets the baseline.
  const lastAnswers = useRef<{date: string; states: Map<string, string>} | null>(null);
  useEffect(() => {
    if (!board || demo) return;
    const states = new Map<string, string>();
    for (const cell of board.cells) {
      for (const c of cell.clients) states.set(`${cell.id}|${c.clientId}`, c.attendance.state);
    }
    const last = lastAnswers.current;
    lastAnswers.current = {date: board.date, states};
    if (!last || last.date !== board.date) return;
    for (const cell of board.cells) {
      for (const c of cell.clients) {
        const {state, source} = c.attendance;
        if (source === "phone" || !(state in WA_REPLY_TR)) continue;
        if (last.states.get(`${cell.id}|${c.clientId}`) === state) continue;
        const text = `WhatsApp: ${c.name} (${cell.time}) ${WA_REPLY_TR[state]}`;
        const go = {label: "Aç", run: () => setOpen({kind: "drawer", anchor: {cell}})};
        if (state === "confirmed") toast.ok(text, go); else toast.warn(text, go);
      }
    }
  }, [board, demo, toast]);

  const providerName = (id: string) =>
    board?.providers.find((p) => p.providerId === id)?.name ?? id;

  // A board refresh replaces the objects; keep the drawer on the same
  // cell or slot by id so a poll does not close what the desk is doing.
  const liveCell = (cell: BoardCell): BoardCell =>
    board?.cells.find((c) => c.id === cell.id) ?? cell;
  const liveClient = (cell: BoardCell, client: BoardClient): BoardClient =>
    liveCell(cell).clients.find((c) => c.clientId === client.clientId) ?? client;
  const liveAnchor = (a: Anchor): Anchor => a.cell ? {cell: liveCell(a.cell)} :
    {rec: board?.cancelled.find((r) => r.cancellationId === a.rec.cancellationId) ?? a.rec};
  const providerOf = (a: Anchor) => providerName(a.cell ? a.cell.providerId : a.rec.providerId);
  const providerPhotoOf = (a: Anchor) => board?.providers.find((p) =>
    p.providerId === (a.cell ? a.cell.providerId : a.rec.providerId))?.photo;
  const back = (a: Anchor) => setOpen({kind: "drawer", anchor: a});

  const drawerActions: DrawerActions = {
    busy: actions.busy,
    onPay: (cell, client) => setOpen({kind: "pay", cell, client}),
    onUndo: (cell, client) => setOpen({kind: "undo", cell, client}),
    onChannel: (cell, client, to) => setOpen({kind: "channel", cell, client, to}),
    onCancelNow: (cell, reason, who) => void actions.cancel(cell, reason, who).then(close),
    onNote: (client) => anchor && setOpen({kind: "note", anchor, client}),
    onClient: (client) => anchor && setOpen({kind: "client", anchor, client}),
    onWhatsapp: (cell, client) => void actions.whatsapp(cell, client),
    onPhoneConfirm: (cell, client, undo) => void actions.phoneConfirm(cell, client, undo),
    onRestore: (cancellationId) => void actions.uncancel(cancellationId).then(close),
    onMove: (cell) => setOpen({kind: "move", cell, time: cell.time, fromDrag: false}),
    onWaSheet: () => anchor && setOpen({kind: "wa", anchor}),
    onSeansInfo: () => anchor && setOpen({kind: "seansInfo", anchor}),
    onHistory: (client) => anchor && setOpen({kind: "history", anchor, client}),
    onNotes: (client) => anchor && setOpen({kind: "notes", anchor, client}),
  };

  // What the column's other seanslar a new start would run into — the
  // grid's preview of the use-case's overlap warning.
  const overlapsOf = (cell: BoardCell) => (time: string): string[] => {
    const start = toMin(time);
    const spans = (board?.cells ?? [])
      .filter((c) => c.providerId === cell.providerId && c.id !== cell.id)
      .map((c) => ({startMin: toMin(c.time), endMin: toMin(c.endTime),
        item: `${c.time}–${c.endTime} ${c.clients.map((x) => x.name).join(", ") || c.title}`}));
    return overlapsAt(spans, start, start + cell.durationMin);
  };

  // The bar's Kasa: the totals for a day that has begun, to a money
  // holder. Ver records a payout, for whoever may (admin — the desk's
  // money:collect does not cover it).
  const money = board?.totals && me.money && date <= today ? board.totals : null;
  const kasaTL = money?.kasa.balanceTL ?? 0;
  const canPay = me.profile === "admin";
  // When the board was put together, Istanbul time: "Kayıtlara göre · 16:40".
  const asOf = board ? new Date(Date.parse(board.generatedAt) + 3 * 3600e3)
    .toISOString().slice(11, 16) : "";
  const ownerName = (money?.payouts.ownerProviderId ?
    providerName(money.payouts.ownerProviderId) : "Seda").split(" ")[0];
  const backLabel = (o: Open) => o?.kind === "provider" ? providerName(o.providerId) : "Kasa";
  // Her seanslar today that nobody has recorded yet: their share is not
  // in what she is handed until they are.
  const pendingOf = (providerId: string): string[] => (board?.cells ?? [])
    .filter((c) => c.providerId === providerId &&
      !["cancelled", "paused", "declined"].includes(c.status) &&
      (c.clients.length === 0 || c.clients.some((x) => !x.recorded)))
    .sort((x, y) => x.time.localeCompare(y.time))
    .map((c) => `${c.clients.filter((x) => !x.recorded).map((x) => x.name).join(", ") ||
      c.title} (${c.time})`);
  const payoutDue = open?.kind === "payout" ?
    money?.payouts.due?.find((d) => d.providerId === open.providerId) : undefined;
  // The seanslar the desk still has to record, in time order.
  const unrecorded = (board?.cells ?? [])
    .filter((c) => {
      const k = seansState(c.status, c.flags).kind;
      return k === "unrecorded" || k === "partial";
    })
    .sort((x, y) => x.time.localeCompare(y.time));

  return (
    <main className="day">
      <nav className="day-nav" aria-label="Gün seçimi">
        <button className="round" onClick={() => setDate(shiftDate(date, -1))} aria-label="Önceki gün">
          <Icon name="back" />
        </button>
        <h2 className="day-title">{formatDateTr(date)}</h2>
        <button className="round" onClick={() => setDate(shiftDate(date, 1))} aria-label="Sonraki gün">
          <Icon name="chev" />
        </button>
        {date === today ?
          <span className="pill soft">bugün</span> :
          <button className="pill" onClick={() => setDate(today)}>Bugün</button>}
        <DatePick date={date} onPick={setDate} />
        {/* Seans ekle and Yeni danışan (Onur, 03.11.2026), for whoever may
            open a card and plan a seans (admin: Onur, bilgi@). Pills after
            the date; on a phone two round icons closing the bar's last row. */}
        {canPay && (
          <>
            <button className="pill add" onClick={() => setOpen({kind: "addSession"})}
              aria-label="Seans ekle" title="Seans ekle">
              <span className="add-wide"><Icon name="plus" />Seans ekle</span>
              <span className="add-narrow"><Icon name="calPlus" /></span>
            </button>
            <button className="pill add"
              onClick={() => setOpen({kind: "addClient", fromSession: false})}
              aria-label="Yeni danışan" title="Yeni danışan">
              <span className="add-wide"><Icon name="plus" />Yeni danışan</span>
              <span className="add-narrow"><Icon name="userPlus" /></span>
            </button>
          </>
        )}
        <span className="spacer" />
        {/* The day's money in one button (Onur, 22.10.2026): what should
            be in the drawer, for whoever may collect it, for a day that
            has begun. It opens the Kasa drawer. */}
        {money && (
          <button className="pill" onClick={() => setOpen({kind: "money"})}>
            <Icon name="wallet" />Kasa
            <span className="soft">{kasaTL < 0 ? `−${tl(-kasaTL)}` : tl(kasaTL)}</span>
          </button>
        )}
        {board?.whatsapp.enabled && board.cells.length > 0 && (
          <button className="pill" onClick={() => setOpen({kind: "waDay"})}>
            <Icon name="msg" />WhatsApp onayları
          </button>
        )}
        <button className="round refresh" onClick={refresh} disabled={busy} aria-busy={busy}
          aria-label="Yenile" title={busy ? "Yenileniyor…" : "Yenile"}>
          <Icon name="refresh" />
        </button>
      </nav>

      {error && <p className="banner error" role="alert">{error}</p>}

      {/* Only what needs doing or knowing; the day's count is gone
          (Onur, 15.10.2026: "8 seans · 7 iptal" not necessary). */}
      {board && (unrecorded.length > 0 || board.warnings.length > 0 || !board.whatsapp.enabled) && (
        <div className="day-alerts">
          {unrecorded.length > 0 && (
            <button className="pill warn" onClick={() => {
              const cell = unrecorded[nextUnrecorded.current % unrecorded.length];
              nextUnrecorded.current += 1;
              setOpen({kind: "drawer", anchor: {cell}});
            }}>
              <Icon name="alert" />{unrecorded.length} seansın kaydı yok<Icon name="chev" />
            </button>
          )}
          {board.warnings.map((w) => <span key={w} className="pill bad">{w}</span>)}
          {!board.whatsapp.enabled && <span className="pill soft">WhatsApp onayları kapalı</span>}
        </div>
      )}

      {!board && !error && <p className="muted">Yükleniyor…</p>}

      {board && (
        <>
          {board.providers.length === 0 ?
            <p className="muted">Takvimi olan aktif danışman yok.</p> :
            <Grid board={board} money={me.money} nowMin={date === today ? nowMin : null}
              today={today}
              onSelectCell={(cell) => setOpen({kind: "drawer", anchor: {cell}})}
              onSelectCancelled={(rec) => setOpen({kind: "drawer", anchor: {rec}})}
              onSelectProvider={(providerId) =>
                setOpen({kind: "provider", providerId, fromMoney: false})}
              onMove={(cell, time) => {
                if (time === null) {
                  toast.warn("Danışman değişikliği sohbetten — hücreyi kendi sütununa bırakın.");
                } else {
                  setOpen({kind: "move", cell, time, fromDrag: true});
                }
              }} />}

          {/* A cancellation sits grey on the grid where its seans was;
              one whose record carries no time has nowhere to sit and
              is listed here instead. */}
          {board.cancelled.some((c) => !c.time) && (
            <section className="list small">
              <b>Saatsiz iptaller:</b>{" "}
              {board.cancelled.filter((c) => !c.time).map((c, i) => (
                <span key={c.cancellationId}>
                  {i > 0 && " · "}
                  <button className="link"
                    onClick={() => setOpen({kind: "drawer", anchor: {rec: c}})}>
                    {c.clientNames.join(", ") || c.eventId} ({providerName(c.providerId)})
                  </button>
                  {" — "}{CANCELLED_BY_TR[c.cancelledBy] ?? c.cancelledBy}: {c.reason}
                </span>
              ))}
            </section>
          )}

          {board.unplanned.some((u) => !u.time) && (
            <section className="list small">
              <b>Saatsiz plansız kayıtlar:</b>{" "}
              {board.unplanned.filter((u) => !u.time).map((u, i) => (
                <span key={u.serviceId}>
                  {i > 0 && " · "}
                  {u.name} ({providerName(u.providerId)}){u.recorded.covered ? " — ödendi" : ""}
                </span>
              ))}
            </section>
          )}

          <p className="muted small">
            Pano {board.generatedAt.slice(11, 19)} UTC'de kuruldu ·{" "}
            {at?.toLocaleTimeString("tr-TR")} okundu · dakikada bir yenilenir
          </p>
        </>
      )}

      {open?.kind === "money" && money && (
        <KasaSheet t={money} when={date === today ? "today" : "past"} date={date} asOf={asOf}
          onClose={close}
          onProvider={(providerId) => setOpen({kind: "provider", providerId, fromMoney: true})}
          onPay={canPay ? (providerId) => setOpen({kind: "payout", providerId, back: open}) :
            undefined}
          onDevir={canPay ? () => setOpen({kind: "devir", back: open}) : undefined} />
      )}
      {open?.kind === "provider" && board && (
        <ProviderSheet board={board} providerId={open.providerId} money={me.money}
          isToday={date === today} onClose={close}
          onBack={open.fromMoney && money ? () => setOpen({kind: "money"}) : undefined}
          onCell={(cell) => setOpen({kind: "drawer", anchor: {cell}})}
          onCancelled={(rec) => setOpen({kind: "drawer", anchor: {rec}})}
          onPay={canPay ?
            () => setOpen({kind: "payout", providerId: open.providerId, back: open}) : undefined}
          onDevir={canPay ? () => setOpen({kind: "devir", back: open}) : undefined} />
      )}
      {open?.kind === "payout" && money && payoutDue && (
        <PayoutSheet name={payoutDue.name} dueTL={payoutDue.payNowTL} kasaTL={kasaTL}
          items={payoutDue.items.every((i) => / seansı$/.test(i.what)) ?
            payoutDue.items.map((i) => i.what.replace(/ seansı$/, "")) : []}
          pending={pendingOf(open.providerId)} estimateTL={payoutDue.unrecorded.estimateTL}
          back={backLabel(open.back)} busy={actions.busy !== null}
          running={actions.busy === `payout:${open.providerId}`}
          onBack={() => setOpen(open.back)} onClose={close}
          onPay={(amountTL, from, key) => {
            const to = open.back;
            void actions.payProvider(open.providerId, amountTL, from, key)
              .then((ok) => ok && setOpen(to));
          }} />
      )}
      {open?.kind === "devir" && money?.payouts.seda && (
        <DevirSheet name={ownerName} safeTL={money.payouts.seda.safeTL}
          owedTL={money.payouts.seda.owedTL} heldTL={money.kasa.heldTL} kasaTL={kasaTL}
          back={backLabel(open.back)} busy={actions.busy !== null}
          running={actions.busy === "devir"}
          onBack={() => setOpen(open.back)} onClose={close}
          onGive={(amountTL, key) => {
            const to = open.back;
            void actions.sedaDevir(amountTL, key).then((ok) => ok && setOpen(to));
          }} />
      )}
      {open?.kind === "waDay" && board && (
        <WaDaySheet board={board} today={today} nowMin={nowMin} busy={actions.busy}
          onSend={actions.whatsappMany} onClose={close} />
      )}
      {open?.kind === "addSession" && board && (
        <AddSessionSheet providers={board.providers} date={date} today={today}
          preset={open.preset} demo={demo ? DEMO_DESK : undefined} busy={actions.busy}
          onClose={close}
          onNewClient={(name) => setOpen({kind: "addClient", name, fromSession: true})}
          onAdd={actions.addSession} />
      )}
      {open?.kind === "addClient" && board && (
        <AddClientSheet providers={board.providers} today={today} initialName={open.name}
          demo={demo ? DEMO_DESK : undefined} busy={actions.busy}
          onBack={open.fromSession ? () => setOpen({kind: "addSession"}) : undefined}
          onClose={close} onCreate={actions.addClient}
          onCreated={(client, guardian) =>
            setOpen({kind: "addSession", preset: {client, welcome: true, guardian}})}
          onPickExisting={(client) =>
            setOpen({kind: "addSession", preset: {client, welcome: false}})} />
      )}
      {open?.kind === "drawer" && (
        <SessionDrawer key={open.anchor.cell?.id ?? open.anchor.rec?.cancellationId}
          anchor={liveAnchor(open.anchor)} providerName={providerOf(open.anchor)}
          providerPhoto={providerPhotoOf(open.anchor)}
          money={me.money} detail={detail.detail} loading={detail.loading}
          error={detail.error} actions={drawerActions} onClose={close}
          today={today} nowMin={nowMin} />
      )}
      {open?.kind === "wa" && (
        <WaSheet anchor={liveAnchor(open.anchor)} detail={detail.detail} loading={detail.loading}
          busy={actions.busy} today={today} nowMin={nowMin} onClose={() => back(open.anchor)}
          onSend={(cell, client) => void actions.whatsapp(cell, client)} />
      )}
      {open?.kind === "seansInfo" && (
        <SeansInfoSheet anchor={liveAnchor(open.anchor)} providerName={providerOf(open.anchor)}
          detail={detail.detail} loading={detail.loading} onClose={() => back(open.anchor)} />
      )}
      {open?.kind === "history" && (
        <HistorySheet anchor={liveAnchor(open.anchor)} client={open.client} detail={detail.detail}
          loading={detail.loading} money={me.money} today={today}
          onClose={() => back(open.anchor)} />
      )}
      {open?.kind === "notes" && (
        <NotesSheet anchor={liveAnchor(open.anchor)} client={open.client} detail={detail.detail}
          loading={detail.loading} busy={actions.busy} onClose={() => back(open.anchor)}
          onNote={(client) => setOpen({kind: "note", anchor: open.anchor, client})} />
      )}
      {open?.kind === "pay" && (
        <PaySheet cell={liveCell(open.cell)} client={liveClient(open.cell, open.client)}
          providerName={providerName(open.cell.providerId)} busy={actions.busy}
          onClose={() => back({cell: open.cell})}
          onPay={(collect) => {
            void actions.pay(open.cell, open.client, collect).then(() => back({cell: open.cell}));
          }} />
      )}
      {open?.kind === "undo" && (
        <UndoRecordSheet cell={liveCell(open.cell)} client={liveClient(open.cell, open.client)}
          providerName={providerName(open.cell.providerId)} busy={actions.busy}
          onClose={() => back({cell: open.cell})}
          onUndo={() => {
            const ids = liveClient(open.cell, open.client).recorded?.serviceIds ?? [];
            void actions.undoRecord(ids, open.client.clientId)
              .then(() => back({cell: open.cell}));
          }} />
      )}
      {open?.kind === "channel" && (
        <ChannelSheet cell={liveCell(open.cell)} client={liveClient(open.cell, open.client)}
          providerName={providerName(open.cell.providerId)} to={open.to} busy={actions.busy}
          onClose={() => back({cell: open.cell})}
          onChange={() => {
            const id = liveClient(open.cell, open.client).recorded?.serviceIds[0];
            if (id) {
              void actions.changeChannel(id, open.client.clientId, open.to)
                .then(() => back({cell: open.cell}));
            }
          }} />
      )}
      {open?.kind === "move" && (
        <MoveSheet cell={liveCell(open.cell)} providerName={providerName(open.cell.providerId)}
          initialTime={open.time} overlapsOf={overlapsOf(open.cell)} busy={actions.busy}
          onClose={() => open.fromDrag ? close() : back({cell: open.cell})}
          onMove={(time) => {
            void actions.move(open.cell, time).then(close);
          }} />
      )}
      {open?.kind === "note" && (
        <NoteSheet client={open.client} busy={actions.busy}
          onClose={() => back(open.anchor)}
          onNote={(text, pin) => {
            void actions.note(open.client, text, pin).then(() => back(open.anchor));
          }} />
      )}
      {open?.kind === "client" && (
        <ClientSheet clientId={open.client.clientId} demo={demo ? DEMO_CLIENT : undefined}
          onClose={() => back(open.anchor)} onSaved={onDone} />
      )}
      {actions.duplicate && (
        <DuplicateDialog ask={actions.duplicate} onClose={actions.dismissDuplicate} />
      )}
    </main>
  );
}
